import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux'
import { FormattedMessage, useIntl } from 'react-intl';
import 'react-phone-number-input/style.css';
import strings from '../../../utils/strings';
import API from '../../../utils/API';
import commonFunctions from '../../../utils/commonFunctions';

function AssignUsersToGroupModal(props) {
  const {
    setResetAssignUsersToGroupModalRef,
    users,
    dataMap,
    loadTable,
    setSuccess,
    closeModal,
    groupsTreeData,
  } = props;

  //Implemented multi-language support
  const intl = useIntl();
  const translation = (id) => intl.formatMessage({ id });

  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [modalError, setModalError] = useState('');
  const [selectAll, setSelectAll] = useState(false);
  const [lastClickedUserId, setLastClickedUserId] = useState(null);

  let currentUserId = props.user.id;

  useEffect(() => {
    setResetAssignUsersToGroupModalRef(resetModal);
  }, [setResetAssignUsersToGroupModalRef]);

  /**
   * Reset the modal state
    */
  const resetModal = () => {
    setSelectedUsers([]);
    setModalError('');
    setSelectAll(false);
    setLastClickedUserId(null);
  };

  // Initialize modal when it's shown
  useEffect(() => {
    const modal = document.getElementById('assignUsersToGroupModal');
    
    if (modal) {
      // Handle modal show event
      const handleModalShow = async () => {
        // Reset state when modal is shown
        resetModal();
        
        // Get all selected group IDs
        const groupIds = Object.keys(dataMap.selectedIds)
          .filter(id => dataMap.selectedIds[id] === 1);
        
        setSelectedGroupIds(groupIds);
        
        if (groupIds.length > 0) {
          try {
            // Fetch the latest user data for these groups
            let allUsers = [];
            let userMap = new Map();
            
            // For each selected group, fetch its users
            for (const groupId of groupIds) {
              const response = await API.getUsersInGroup(groupId);
              
              if (response.data && Array.isArray(response.data)) {
                response.data.forEach(user => {
                  // Add group to user's groups array
                  if (!userMap.has(user.id)) {
                    user.groups = [{ id: groupId }];
                    userMap.set(user.id, user);
                    allUsers.push(user);
                  } else {
                    const existingUser = userMap.get(user.id);
                    existingUser.groups.push({ id: groupId });
                  }
                });
              }
            }
            
            // Find users who are assigned to ALL of the selected groups
            const preSelectedUsers = allUsers.filter(user => {
              // For multiple groups, user must be in ALL selected groups
              if (groupIds.length > 1) {
                // Convert user groups to array of IDs
                const userGroupIds = user.groups.map(group => group.id || group);
                // Check if user belongs to all selected groups
                return groupIds.every(groupId => userGroupIds.includes(groupId));
              } else {
                // For single group, all users are pre-selected
                return true;
              }
            }).map(user => user.id);
            
            console.log('Pre-selected users:', preSelectedUsers);
            setSelectedUsers(preSelectedUsers);
            
            // Update selectAll if all users are selected
            if (preSelectedUsers.length === allUsers.length && allUsers.length > 0) {
              setSelectAll(true);
            }
          } catch (error) {
            console.error('Error fetching users for groups:', error);
          }
        }
      };
      
      // Add event listener for modal show
      modal.addEventListener('show.bs.modal', handleModalShow);
      
      // Clean up event listener
      return () => {
        modal.removeEventListener('show.bs.modal', handleModalShow);
      };
    }
  }, [dataMap, resetModal]);

  // Update selectAll state when selectedUsers changes
  useEffect(() => {
    if (users && users.length > 0) {
      // If all users are selected, set selectAll to true
      setSelectAll(selectedUsers.length === users.length);
    }
  }, [selectedUsers, users]);

  function setSelectedUser(e, userId) {
    // Store the actual user ID, not the event
    const clickedUserId = userId || e.target.value;
    
    // Prevent deselection of current user
    if (clickedUserId === currentUserId) {
      return;
    }
    
    // Handle shift-click selection
    if (e.shiftKey && lastClickedUserId && users) {
      console.log('handleShiftClickSelection', users, selectedUsers, clickedUserId, lastClickedUserId)
      const newSelectedUsers = commonFunctions.handleShiftClickSelection(
        users,
        selectedUsers,
        clickedUserId,
        lastClickedUserId
      );
      
      // Ensure current user remains selected
      if (currentUserId && !newSelectedUsers.includes(currentUserId)) {
        newSelectedUsers.push(currentUserId);
      }
      
      setSelectedUsers(newSelectedUsers);
    } else {
      // Regular single selection
      let newArray = [...selectedUsers];
      const index = newArray.indexOf(clickedUserId);
      
      if (index !== -1) {
        // If already selected, remove it
        newArray.splice(index, 1);
      } else {
        // If not selected, add it
        newArray.push(clickedUserId);
      }
      setSelectedUsers(newArray);
    }
    
    // Update the last clicked user ID
    setLastClickedUserId(clickedUserId);
  }

  function handleSelectAll(e) {
    setSelectAll(e.target.checked);
    if (e.target.checked) {
      setSelectedUsers(users.map(user => user.id));
    } else {
      // When deselecting all, keep current user selected
      setSelectedUsers(currentUserId ? [currentUserId] : []);
    }
    // Reset last clicked user when selecting/deselecting all
    setLastClickedUserId(null);
  }

  const handleAssignUsersToGroup = async (e) => {
    e.preventDefault();
    
    if (selectedGroupIds.length === 0) {
      setModalError(<FormattedMessage id="NO_GROUPS_SELECTED" />);
      return;
    }

    if (selectedUsers.length === 0 && users.length > 0) {
      // Confirm if the user wants to deassign all users from the selected groups
      if (!window.confirm(translation('CONFIRM_DEASSIGN_ALL_USERS'))) {
        return;
      }
    }

    try {
      // Get the users to assign (currently selected)
      const usersToAssign = selectedUsers;
      
      // Get the users to deassign (not selected but might have been previously assigned)
      const usersToDeassign = users
        .filter(user => !selectedUsers.includes(user.id) && 
                        user.groups && 
                        Array.isArray(user.groups) && 
                        user.groups.some(group => selectedGroupIds.includes(group.id || group)))
        .map(user => user.id);
      
      console.log('Users to assign:', usersToAssign);
      console.log('Users to deassign:', usersToDeassign);

      // First, unbind users that need to be deassigned
      if (usersToDeassign.length > 0) {
        console.log('Unbinding users from groups:', selectedGroupIds);
        await API.unbindUsersFromGroups(selectedGroupIds);
      }
      
      // Then, bind the selected users to the groups
      if (usersToAssign.length > 0) {
        console.log('Binding users to groups:', selectedGroupIds);
        console.log('usersToAssign', usersToAssign);
        console.log('selectedGroupIds', selectedGroupIds);
        await API.bindUsersToGroups(usersToAssign, selectedGroupIds);
      }
      
      closeModal("assignUsersToGroupModal");
      resetModal();
      loadTable(dataMap);
      
      setSuccess(<FormattedMessage id="ASSIGN_USERS_TO_GROUP_SUCCESS" />);
      // Show appropriate success message based on what was done
      // if (usersToAssign.length > 0 && usersToDeassign.length > 0) {
      //   setSuccess(<FormattedMessage id="ASSIGN_DEASSIGN_USERS_SUCCESS" />);
      // } else if (usersToAssign.length > 0) {
      //   setSuccess(<FormattedMessage id="ASSIGN_USERS_TO_GROUP_SUCCESS" />);
      // } else if (usersToDeassign.length > 0) {
      //   setSuccess(<FormattedMessage id="DEASSIGN_USERS_FROM_GROUP_SUCCESS" />);
      // } else {
      //   setSuccess(<FormattedMessage id="NO_CHANGES_MADE" />);
      // }
    } catch (err) {
      console.error(`Failed to update user group assignments: ${err}`);
      setModalError(
        <FormattedMessage 
          id="ASSIGN_USERS_TO_GROUP_FAILED" 
          values={{ errorMsg: <b>{err.message}</b> }} 
        />
      );
    }
  };

  // Ensure current user is always in selectedUsers
  useEffect(() => {
    if (currentUserId && !selectedUsers.includes(currentUserId)) {
      setSelectedUsers(prev => [...prev, currentUserId]);
    }
  }, [currentUserId, selectedUsers]);

  return (
    <div className="modal modal-mse fade" id="assignUsersToGroupModal" tabIndex="-1" aria-labelledby="assignUsersToGroupModalLabel" aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content modal-mes-content">
          <div className="modal-header modal-mes-header" style={{ backgroundColor: '#347BFA' }}>
            <h5 className="modal-title modal-mes-title"><FormattedMessage id="ASSIGN_USERS_TO_GROUP" /></h5>
            <button id="closeAssignUsersToGroupModal" type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div className="modal-body">
            <div className="card" style={{ borderRadius: '8px', overflow: 'hidden' }}>
              <div style={{ overflowX: 'hidden' }}>
                <table className="table">
                  <thead>
                    <tr style={{ backgroundColor: '#E9F3FC' }}>
                      <th className="mesCustomHeaderCell" scope="col">
                        <input
                          id="selectAll"
                          type="checkbox"
                          className="form-check-input"
                          checked={selectAll}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th className="mesCustomHeaderCell" scope="col"><FormattedMessage id="FIRST_NAME" /></th>
                      <th className="mesCustomHeaderCell" scope="col"><FormattedMessage id="LAST_NAME" /></th>
                      <th className="mesCustomHeaderCell" scope="col"><FormattedMessage id="EMAIL" /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {users && users.length > 0 ? (
                      commonFunctions.getUsersSort(users).map((user) => {
                        const isCurrentUser = user.id === currentUserId;
                        return (
                          <tr key={user.id} className={isCurrentUser ? "text-muted" : ""}>
                            <th className="mesCustomBodyCell" scope="row">
                              <input
                                id="userCheckbox"
                                className="form-check-input"
                                type="checkbox"
                                value={user.id}
                                checked={selectedUsers.includes(user.id) || isCurrentUser}
                                onChange={(e) => { setSelectedUser(e, user.id); }}
                                disabled={isCurrentUser}
                              />
                            </th>                        
                            <td className="mesCustomBodyCell">
                              {user.firstName}
                            </td>
                            <td className="mesCustomBodyCell">{user.lastName}</td>
                            <td className="mesCustomBodyCell">{user.email}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center">
                          <FormattedMessage id="NO_USERS_IN_GROUP" />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {modalError && (
              <div className="modal-error invalid-feedback d-block fw-bold text-center">
                {modalError}
              </div>
            )}
          </div>
          <div className="modal-footer modal-mes-footer d-flex align-items-center">
            <button id="backAssignUsersToGroupModalBtn" className="btn btn-outline-secondary me-3" type="button" data-bs-dismiss="modal"><FormattedMessage id="BACK" /></button>
            <button 
              id="saveAssignUsersToGroupBtn" 
              className="btn btn-primary but-darkblue" 
              type="button" 
              onClick={handleAssignUsersToGroup}
              disabled={selectedUsers.length === 0 || selectedGroupIds.length === 0}
            >
              <FormattedMessage id="SAVE" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
} 

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps)(AssignUsersToGroupModal);